import React, { useEffect, useState } from 'react';

// No utility imports needed - components handle their own utilities

// Custom hooks
import { useCourseData } from './hooks/useCourseData.js';
import { useCourseSelection } from './hooks/useCourseSelection.js';
import { useCreditTracking } from './hooks/useCreditTracking.js';
import { useUrlState } from './hooks/useUrlState.js';
import { useDragAndDrop } from './hooks/useDragAndDrop.js';

// Components
import ProgramMeta from './components/ProgramMeta.js';
import SemesterSection from './components/SemesterSection.js';
import AddCourseDialog from './components/AddCourseDialog.js';
import ProgramProgress from './components/ProgramProgress.js';
import SimplifiedCourseTable from './components/SimplifiedCourseTable.js';
import TimetableView from './components/TimetableView.js';
import { Box } from '@mui/material';

// CourseRow component now imported from components folder

const CourseBoard = () => {
  // Custom hooks for state management
  const { groupedCourses, courseOrders, setGroupedCourses, setCourseOrders } = useCourseData();
  
  // Initialize credit tracking with empty selectedIds initially
  const { semesterCredits } = useCreditTracking(groupedCourses, new Set());
  
  // Initialize course selection with dependencies
  const { 
    selectedIds, 
    backupIds, 
    selectedCourses, 
    setSelectedIds, 
    setBackupIds, 
    toggleSelect 
  } = useCourseSelection(groupedCourses, semesterCredits);
  
  // Update credit tracking with actual selectedIds
  const creditTracking = useCreditTracking(groupedCourses, selectedIds);
  const actualSemesterCredits = creditTracking.semesterCredits;
  const actualPeriodCredits = creditTracking.periodCredits;
  
  // UI state (kept local as it's simple)
  const [minimizedPeriods, setMinimizedPeriods] = useState({}); // Track which periods are minimized
  const [viewMode, setViewMode] = useState('selection'); // Current view mode: 'selection', 'review', or 'timetable'
  
  // URL state management
  const { urlState, setUrlState, copyUrlToClipboard } = useUrlState(groupedCourses, selectedIds, backupIds, viewMode);
  
  // Drag and drop functionality
  const { sensors, handleDragEnd } = useDragAndDrop(groupedCourses, setGroupedCourses, courseOrders, setCourseOrders);
  
  // Add course dialog state (simplified for component-based approach)
  const [addCourseDialog, setAddCourseDialog] = useState({
    open: false,
    prefilledData: {}
  });

  // Course data processing now handled by useCourseData hook

  // Credit calculation now handled by useCreditTracking hook

  const sortedKeys = Object.keys(groupedCourses).sort((a, b) => {
    const [yearA, sA, pA] = a.split(/_s|_p/).map(Number);
    const [yearB, sB, pB] = b.split(/_s|_p/).map(Number);
    return yearA - yearB || sA - sB || pA - pB;
  });

  // Sensors now provided by useDragAndDrop hook

  // handleDragEnd now provided by useDragAndDrop hook

  // toggleSelect now provided by useCourseSelection hook

  // isCourseDisabled now imported from utils

  // Date utility functions now imported from utils

  // Helper function to toggle period minimize state
  const togglePeriodMinimize = (periodKey) => {
    setMinimizedPeriods(prev => ({
      ...prev,
      [periodKey]: !prev[periodKey]
    }));
  };

  // getValidSemesters now imported from utils

  // URL state management now handled by useUrlState hook

  // Add course dialog handlers
  const openAddCourseDialog = (year, semester, period) => {
    setAddCourseDialog({
      open: true,
      prefilledData: { year, semester, period }
    });
  };

  const handleDialogClose = () => {
    setAddCourseDialog(prev => ({
      ...prev,
      open: false
    }));
  };

  const handleAddCourse = (newCourse, formData) => {
    // Add course to grouped courses
    setGroupedCourses(prev => {
      const updated = { ...prev };
      
      formData.periods.forEach((period, periodIndex) => {
        const groupKey = `${formData.year}_s${formData.semester}_p${period}`;
        
        if (!updated[groupKey]) {
          updated[groupKey] = {
            year: parseInt(formData.year),
            semester: parseInt(formData.semester),
            period: period,
            courses: []
          };
        }

        const courseEntry = {
          ...newCourse,
          credits: formData.periods.length > 1 
            ? parseFloat(formData.credits) / formData.periods.length
            : parseFloat(formData.credits),
          original_credits: parseFloat(formData.credits),
          period_index: periodIndex,
          actual_period: period,
          is_split_course: formData.periods.length > 1,
          period_credits: formData.periods.length > 1 
            ? formData.periods.map(() => parseFloat(formData.credits) / formData.periods.length)
            : undefined,
          semester_instance: `${formData.course_code}_y${formData.year}_s${formData.semester}`
        };

        updated[groupKey].courses.push(courseEntry);
      });
      
      return updated;
    });
  };

  // Apply URL state after courses are loaded and URL state is available
  useEffect(() => {
    if (!urlState || Object.keys(groupedCourses).length === 0) return;
    
    const { selectedSet, backupSet, customCourses, viewMode: urlViewMode } = urlState;
    
    // Set view mode from URL if it exists
    if (urlViewMode) {
      setViewMode(urlViewMode);
    }
    
    // First, add custom courses to grouped courses if they exist
    if (customCourses.length > 0) {
      setGroupedCourses(prev => {
        const updated = { ...prev };
        
        customCourses.forEach(course => {
          // Process each instance of the custom course
          course.instances.forEach((instance) => {
            const { year, semester, periods, period_credits } = instance;
            
            // Handle courses with period_credits (spanning multiple periods)
            if (period_credits && period_credits.length > 0) {
              periods.forEach((period, periodIndex) => {
                const groupKey = `${year}_s${semester}_p${period}`;
                
                if (!updated[groupKey]) {
                  updated[groupKey] = {
                    year,
                    semester,
                    period,
                    courses: [],
                  };
                }

                // Create a course entry with period-specific credits and unique semester ID
                const courseEntry = {
                  ...course,
                  credits: period_credits[periodIndex],
                  original_credits: course.credits,
                  period_index: periodIndex,
                  actual_period: period,
                  is_split_course: true,
                  period_credits: period_credits,
                  semester_instance: `${course.course_code}_y${year}_s${semester}`
                };

                updated[groupKey].courses.push(courseEntry);
              });
            } else {
              // Handle single-credit courses (no period_credits)
              periods.forEach((period) => {
                const groupKey = `${year}_s${semester}_p${period}`;

                if (!updated[groupKey]) {
                  updated[groupKey] = {
                    year,
                    semester,
                    period,
                    courses: [],
                  };
                }

                // Create a course entry with unique semester ID
                const courseEntry = {
                  ...course,
                  is_split_course: false,
                  semester_instance: `${course.course_code}_y${year}_s${semester}`
                };

                updated[groupKey].courses.push(courseEntry);
              });
            }
          });
        });
        
        return updated;
      });
    }
    
    // Then set selection state
    if (selectedSet.size > 0 || backupSet.size > 0) {
      setSelectedIds(selectedSet);
      setBackupIds(backupSet);
    }
    
    // Clear URL state to prevent re-application
    setUrlState(null);
  }, [urlState, groupedCourses, setSelectedIds, setBackupIds, setGroupedCourses, setUrlState, setViewMode]); // Run when both URL state and courses are available

  // Dialog handlers now simplified for component-based approach

  // Helper functions now imported from utils

  // Group keys by semester for displaying credit totals
  const semesterGroups = {};
  sortedKeys.forEach(key => {
    const group = groupedCourses[key];
    const semesterKey = `${group.year}_s${group.semester}`;
    if (!semesterGroups[semesterKey]) {
      semesterGroups[semesterKey] = {
        year: group.year,
        semester: group.semester,
        groups: []
      };
    }
    semesterGroups[semesterKey].groups.push(key);
  });

  return (
    <>
      {/* Disclaimer Banner */}
      <Box
        sx={{
          backgroundColor: '#232323',
          color: 'white',
          textAlign: 'center',
          py: 0.5,
          fontSize: '0.75rem',
          fontWeight: 500,
          fontStyle: 'italic'
        }}
      >
        This page is for demo purposes only; It is in no way associated with Uppsala University.
      </Box>
      
      <Box px={4} pt={4} pb={0} display="flex" flexDirection="column" gap={3}>
      {/* Program Meta Information */}
      <ProgramMeta 
        viewMode={viewMode} 
        onViewModeChange={setViewMode} 
      />

      {/* Dynamic View Based on Mode */}
      {viewMode === 'review' ? (
        <SimplifiedCourseTable
          groupedCourses={groupedCourses}
          selectedIds={selectedIds}
          backupIds={backupIds}
        />
      ) : viewMode === 'timetable' ? (
        <TimetableView
          groupedCourses={groupedCourses}
          selectedIds={selectedIds}
          backupIds={backupIds}
        />
      ) : (
        // Default: Course Selection Interface
        Object.keys(semesterGroups).sort((a, b) => {
          // Extract semester numbers from keys like "2025_s1" and "2026_s2"
          const semesterA = parseInt(a.split('_s')[1]);
          const semesterB = parseInt(b.split('_s')[1]);
          return semesterA - semesterB;
        }).map((semesterKey) => {
          const semesterInfo = semesterGroups[semesterKey];
          const credits = actualSemesterCredits[semesterKey] || 0;
          
          return (
            <SemesterSection
              key={semesterKey}
              semesterKey={semesterKey}
              semesterInfo={semesterInfo}
              semesterCredits={credits}
              groupedCourses={groupedCourses}
              periodCredits={actualPeriodCredits}
              minimizedPeriods={minimizedPeriods}
              onTogglePeriodMinimize={togglePeriodMinimize}
              sensors={sensors}
              onDragEnd={handleDragEnd}
              selectedIds={selectedIds}
              backupIds={backupIds}
              selectedCourses={selectedCourses}
              onToggleSelect={toggleSelect}
              onAddCourse={openAddCourseDialog}
              allSemesterCredits={actualSemesterCredits}
            />
          );
        })
      )}

      {/* Program Progress */}
      <ProgramProgress 
        totalCredits={creditTracking.getTotalCredits()}
        onCopyLink={copyUrlToClipboard}
      />

      {/* Add Course Dialog */}
      <AddCourseDialog
        open={addCourseDialog.open}
        onClose={handleDialogClose}
        onAddCourse={handleAddCourse}
        prefilledData={addCourseDialog.prefilledData}
      />
    </Box>
    </>
  );
};

export default CourseBoard;